import {
  graphIds,
  hasRole,
  workflowDefinitionSchema,
  type AgentDto,
  type Role,
  type SandboxDto,
  type ToolDto,
  type WorkflowDefinition,
  type WorkflowDto,
  type WorkflowEntryType,
  type WorkflowRunCounts,
  type WorkflowRunDto,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus, Workflow } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { SELECT_INPUT, SELECT_OUTPUT, WorkflowGraph } from '@/components/workflow-graph';
import { WorkflowRunPanel } from '@/components/workflow-run-panel';
import { WorkflowSchedules } from '@/components/workflow-schedules';
import { InputsEditor, StepInspector } from '@/components/workflow-step-editor';
import { api, ApiError } from '@/lib/api';
import { formatDateTime, formatDuration, formatRelative } from '@/lib/format';
import { usePaged } from '@/lib/queries';
import {
  CONTAINER_ENTRY_TYPES,
  ENTRY_META,
  fromResults,
  LEAF_TYPES,
  locate,
  newEntry,
  STARTER,
  WORKFLOW_STATUS_LABEL,
  WORKFLOW_STATUS_VARIANT,
  type LiveStep,
  type WorkflowResources,
} from '@/lib/workflow-ui';
import {
  append,
  looseEntries,
  mainChain,
  withGraph,
  type CanvasModel,
} from '@/lib/workflow-canvas';

export { WORKFLOW_STATUS_VARIANT } from '@/lib/workflow-ui';

function NewWorkflowDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<WorkflowDto>('/workflows', {
        method: 'POST',
        body: { name, description, definition: STARTER },
      }),
    onSuccess: (w) => {
      toast.success(`Workflow ${w.name} created`);
      void queryClient.invalidateQueries({ queryKey: ['workflows', tenantId] });
      setOpen(false);
      void navigate({ to: '/workflows/$workflowId', params: { workflowId: w.id } });
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>New workflow</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a workflow</DialogTitle>
          <DialogDescription>
            Chain agents, tools, sandboxes and other workflows, with parallel steps, branches,
            loops, sleeps and human approval. You start with one step.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <ErrorAlert error={create.error} />
          <div className="grid gap-1.5">
            <Label htmlFor="wf-name">Name</Label>
            <Input
              id="wf-name"
              className="font-mono"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="research-and-summarize"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="wf-description">Description</Label>
            <Input
              id="wf-description"
              value={description}
              placeholder="Shown to agents that use this workflow as a tool"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Create workflow
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StepChips({ definition }: { definition: WorkflowDefinition }) {
  const ids = definition.graph.map((e) => e.id);
  return (
    <div className="flex flex-wrap gap-1">
      {definition.graph.slice(0, 8).map((entry) => {
        const meta = ENTRY_META[entry.type];
        return (
          <span
            key={entry.id}
            className="bg-muted inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs"
            title={`${meta.label}: ${entry.name}`}
          >
            <meta.icon className="size-3" aria-hidden /> {entry.id}
          </span>
        );
      })}
      {ids.length > 8 && (
        <span className="text-muted-foreground text-xs">+{ids.length - 8} more</span>
      )}
    </div>
  );
}

function WorkflowList({ tenantId, role }: { tenantId: string; role: Role }) {
  const workflows = useQuery({
    queryKey: ['workflows', tenantId],
    queryFn: () => api<{ items: WorkflowDto[] }>('/workflows'),
  });
  const counts = useQuery({
    queryKey: ['workflow-run-counts', tenantId],
    queryFn: () => api<WorkflowRunCounts>('/workflows/run-counts'),
  });
  const rows = workflows.data?.items ?? [];
  const search = useListSearch(rows, (w) => [w.name, w.description]);
  return (
    <>
      <PageHeader
        title="Workflows"
        description="Durable pipelines over agents, tools, sandboxes and other workflows — with branches, loops, sleeps and human approval. Agents can run them as tools."
        actions={hasRole(role, 'admin') && <NewWorkflowDialog tenantId={tenantId} />}
      />
      <ErrorAlert error={workflows.error} />
      {workflows.isPending ? (
        <Skeleton className="h-32" />
      ) : rows.length === 0 ? (
        <EmptyState title="No workflows yet">
          A workflow passes data from step to step: research with one agent, compute in a sandbox,
          wait for approval, then write with another.
        </EmptyState>
      ) : (
        <>
          <ListSearch
            className="mb-4"
            value={search.query}
            onChange={search.setQuery}
            label="Search workflows"
            placeholder="Search by name or description"
          />
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Steps</TableHead>
                    <TableHead>Runs</TableHead>
                    <TableHead>Last run</TableHead>
                    <TableHead className="text-right">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {search.filtered.map((w) => {
                    const c = counts.data?.[w.id];
                    return (
                      <TableRow key={w.id}>
                        <TableCell>
                          <Link
                            to="/workflows/$workflowId"
                            params={{ workflowId: w.id }}
                            className="flex items-center gap-2 font-mono hover:underline"
                          >
                            <Workflow className="size-4" aria-hidden /> {w.name}
                          </Link>
                          {w.description && (
                            <p className="text-muted-foreground text-xs">{w.description}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <StepChips definition={w.definition} />
                        </TableCell>
                        <TableCell className="text-xs">
                          {c ? (
                            <span className="flex flex-wrap gap-x-2">
                              <span>{c.total} total</span>
                              {c.running > 0 && <span>{c.running} active</span>}
                              {c.suspended > 0 && (
                                <span className="text-amber-600 dark:text-amber-400">
                                  {c.suspended} need input
                                </span>
                              )}
                              {c.failed > 0 && (
                                <span className="text-destructive">{c.failed} failed</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {w.lastRun ? (
                            <Link
                              to="/traces/workflows/$runId"
                              params={{ runId: w.lastRun.id }}
                              className="flex items-center gap-2 text-xs hover:underline"
                            >
                              <Badge variant={WORKFLOW_STATUS_VARIANT[w.lastRun.status]}>
                                {WORKFLOW_STATUS_LABEL[w.lastRun.status]}
                              </Badge>
                              {formatRelative(w.lastRun.startedAt)}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground text-xs">Never run</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right text-xs">
                          {formatRelative(w.updatedAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {search.filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <NoMatches query={search.query} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}

export function WorkflowsPage() {
  return (
    <RequireTenant>
      {(tenant) => <WorkflowList tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}

// --- Editor ---

function RunHistory({
  tenantId,
  workflowId,
  activeRunId,
  onPick,
}: {
  tenantId: string;
  workflowId: string;
  activeRunId: string | null;
  onPick: (run: WorkflowRunDto) => void;
}) {
  const runs = usePaged<WorkflowRunDto>(
    ['workflow-runs', tenantId, workflowId],
    `/workflow-runs?workflowId=${workflowId}`,
  );
  const rows = runs.data?.pages.flatMap((p) => p.items) ?? [];
  if (runs.isPending) return <Skeleton className="h-24" />;
  if (rows.length === 0) return <p className="text-muted-foreground text-sm">No runs yet.</p>;
  return (
    <div className="grid gap-2">
      <p className="text-muted-foreground text-xs">
        Pick a run to show it on the graph, continue it, or re-run it from a step.
      </p>
      <ol className="grid gap-1.5">
        {rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => onPick(r)}
              aria-current={r.id === activeRunId}
              className="hover:bg-muted aria-[current=true]:bg-muted flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs"
            >
              <Badge variant={WORKFLOW_STATUS_VARIANT[r.status]}>
                {WORKFLOW_STATUS_LABEL[r.status]}
              </Badge>
              <span>{formatDateTime(r.startedAt)}</span>
              {r.trigger !== 'console' && (
                <span className="text-muted-foreground">by {r.trigger}</span>
              )}
              <span className="text-muted-foreground ml-auto">{formatDuration(r.latencyMs)}</span>
            </button>
          </li>
        ))}
      </ol>
      {runs.hasNextPage && (
        <Button
          size="sm"
          variant="ghost"
          className="justify-self-start"
          disabled={runs.isFetchingNextPage}
          onClick={() => void runs.fetchNextPage()}
        >
          Load more
        </Button>
      )}
    </div>
  );
}

function WorkflowEditor({
  tenantId,
  role,
  workflowId,
}: {
  tenantId: string;
  role: Role;
  workflowId: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canEdit = hasRole(role, 'admin');
  const workflow = useQuery({
    queryKey: ['workflow', tenantId, workflowId],
    queryFn: () => api<WorkflowDto>(`/workflows/${workflowId}`),
  });
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const tools = useQuery({
    queryKey: ['tools', tenantId],
    queryFn: () => api<{ items: ToolDto[] }>('/tools'),
  });
  const sandboxes = useQuery({
    queryKey: ['sandboxes', tenantId],
    queryFn: () => api<{ items: SandboxDto[] }>('/sandboxes'),
  });
  const workflows = useQuery({
    queryKey: ['workflows', tenantId],
    queryFn: () => api<{ items: WorkflowDto[] }>('/workflows'),
  });
  const resources: WorkflowResources = {
    agents: agents.data?.pages.flatMap((p) => p.items) ?? [],
    tools: tools.data?.items ?? [],
    sandboxes: sandboxes.data?.items ?? [],
    workflows: workflows.data?.items ?? [],
  };
  const [draft, setDraft] = useState<WorkflowDefinition | null>(null);
  /** Canvas steps that are not linked to the workflow (see `CanvasModel`). */
  const [breaks, setBreaks] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<string>(SELECT_INPUT);
  const [tab, setTab] = useState('edit');
  const [live, setLive] = useState<Record<string, LiveStep>>({});
  const [run, setRun] = useState<WorkflowRunDto | null>(null);
  const [loadRunId, setLoadRunId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (workflow.data) {
      setDraft(workflow.data.definition);
      setBreaks([]);
      setDescription(workflow.data.description);
    }
  }, [workflow.data]);
  const onLive = useCallback((next: Record<string, LiveStep>) => setLive(next), []);
  const onRun = useCallback((next: WorkflowRunDto | null) => setRun(next), []);

  const save = useMutation({
    mutationFn: (definition: WorkflowDefinition) =>
      api<WorkflowDto>(`/workflows/${workflowId}`, {
        method: 'PATCH',
        body: { definition, description },
      }),
    onSuccess: (w) => {
      toast.success('Workflow saved');
      queryClient.setQueryData(['workflow', tenantId, workflowId], w);
      void queryClient.invalidateQueries({ queryKey: ['workflows', tenantId] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api<void>(`/workflows/${workflowId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workflows', tenantId] });
      void navigate({ to: '/workflows' });
    },
  });

  // What is saved and runs: the steps linked from Input.
  const saved = useMemo(
    () => (draft ? { ...draft, graph: mainChain({ graph: draft.graph, breaks }) } : null),
    [draft, breaks],
  );
  const validation = useMemo(
    () => (saved ? workflowDefinitionSchema.safeParse(saved) : null),
    [saved],
  );
  if (workflow.error) return <ErrorAlert error={workflow.error} />;
  if (!workflow.data || !draft || !saved) return <Skeleton className="h-96" />;
  const w = workflow.data;
  const model: CanvasModel = { graph: draft.graph, breaks };
  const loose = looseEntries(model);
  const dirty =
    breaks.length > 0 ||
    JSON.stringify(saved) !== JSON.stringify(w.definition) ||
    description !== w.description;
  const setModel = (next: CanvasModel) => {
    setDraft({ ...draft, graph: next.graph });
    setBreaks(next.breaks);
  };
  // While inspecting a run, the graph shows the definition that run used.
  const showingRun = tab !== 'edit' && tab !== 'schedules' && run !== null;
  const graphDefinition = showingRun ? run.definition : draft;
  const selectedInGraph =
    selected === SELECT_INPUT || selected === SELECT_OUTPUT || locate(graphDefinition, selected)
      ? selected
      : SELECT_INPUT;

  const create = (type: WorkflowEntryType) => newEntry(type, draft, resources, w.id);
  const add = (type: WorkflowEntryType) => {
    const entry = create(type);
    setModel(append(model, entry));
    setSelected(entry.id);
    setTab('edit');
  };

  return (
    <>
      <PageHeader
        title={w.name}
        description={w.description || 'Workflow'}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/workflows">All workflows</Link>
            </Button>
            {canEdit && (
              <>
                <Button variant="destructive" onClick={() => setDeleting(true)}>
                  Delete
                </Button>
                <ConfirmDeleteDialog
                  open={deleting}
                  onOpenChange={setDeleting}
                  title={`Delete ${w.name}?`}
                  description="The workflow, its schedules and its run history are deleted. This cannot be undone."
                  onConfirm={() => remove.mutateAsync()}
                />
                <Button
                  disabled={!dirty || save.isPending || !validation?.success || loose.length > 0}
                  title={
                    loose.length > 0 ? 'Link or delete the unconnected steps first' : undefined
                  }
                  onClick={() => save.mutate(saved)}
                >
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="grid content-start gap-3">
          {showingRun && (
            <p className="text-muted-foreground text-xs">
              Showing the run of {formatDateTime(run.startedAt)} (the workflow as it was then).
            </p>
          )}
          <WorkflowGraph
            workflowId={w.id}
            model={showingRun ? { graph: run.definition.graph, breaks: [] } : model}
            inputs={graphDefinition.inputs}
            hasOutput={Boolean(graphDefinition.output)}
            editable={canEdit && !showingRun}
            fitKey={showingRun ? 'run' : 'draft'}
            selected={selectedInGraph}
            live={tab === 'edit' || tab === 'schedules' ? {} : live}
            onSelect={(id) => {
              setSelected(id);
              if (tab === 'schedules') setTab('edit');
            }}
            onChange={setModel}
            onCreate={create}
          />
          {loose.length > 0 && (
            <p
              role="status"
              className="rounded-md border border-dashed border-amber-500/60 p-2 text-xs text-amber-700 dark:text-amber-400"
            >
              Not connected (does not run): {loose.map((e) => e.name).join(', ')}. Link{' '}
              {loose.length === 1 ? 'it' : 'them'} from a step’s ● handle, or delete{' '}
              {loose.length === 1 ? 'it' : 'them'}, to save.
            </p>
          )}
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-self-start">
                  <Plus aria-hidden /> Add step
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72">
                <DropdownMenuLabel>Steps</DropdownMenuLabel>
                {LEAF_TYPES.map((type) => {
                  const meta = ENTRY_META[type];
                  return (
                    <DropdownMenuItem key={type} onSelect={() => add(type)}>
                      <meta.icon aria-hidden />
                      <span className="grid">
                        <span>{meta.label}</span>
                        <span className="text-muted-foreground text-xs">{meta.hint}</span>
                      </span>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Control flow</DropdownMenuLabel>
                {CONTAINER_ENTRY_TYPES.map((type) => {
                  const meta = ENTRY_META[type];
                  return (
                    <DropdownMenuItem key={type} onSelect={() => add(type)}>
                      <meta.icon aria-hidden />
                      <span className="grid">
                        <span>{meta.label}</span>
                        <span className="text-muted-foreground text-xs">{meta.hint}</span>
                      </span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <ErrorAlert error={save.error} />
          {save.error instanceof ApiError && save.error.errors.length > 0 && (
            <ul className="text-destructive list-disc pl-5 text-sm">
              {save.error.errors.map((e) => (
                <li key={e.path + e.message}>
                  <span className="font-mono">{e.path}</span>: {e.message}
                </li>
              ))}
            </ul>
          )}
          {validation && !validation.success && (
            <ul className="text-destructive list-disc pl-5 text-sm" aria-label="Problems">
              {validation.error.issues.slice(0, 6).map((i, n) => (
                <li key={n}>
                  <span className="font-mono">{i.path.join('.')}</span>: {i.message}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Card className="self-start">
          <Tabs value={tab} onValueChange={setTab}>
            <CardHeader>
              <TabsList>
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="run">Run</TabsTrigger>
                <TabsTrigger value="history">Runs</TabsTrigger>
                <TabsTrigger value="schedules">Schedules</TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent className="max-h-[calc(100dvh-14rem)] overflow-y-auto">
              <TabsContent value="edit" className="grid gap-4">
                {selected === SELECT_INPUT ? (
                  <>
                    <CardTitle className="text-base">Inputs</CardTitle>
                    <InputsEditor
                      inputs={draft.inputs}
                      disabled={!canEdit}
                      onChange={(inputs) => setDraft({ ...draft, inputs })}
                      hint={
                        <>
                          Values you provide when starting a run (and the arguments agents fill in
                          when they call this workflow as a tool), read as{' '}
                          <code>{'{{input.name}}'}</code>.
                        </>
                      }
                    />
                    <div className="grid gap-1.5">
                      <Label htmlFor="wf-edit-description">Workflow description</Label>
                      <Textarea
                        id="wf-edit-description"
                        value={description}
                        disabled={!canEdit}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                      <p className="text-muted-foreground text-xs">
                        Agents see this when deciding to run the workflow.
                      </p>
                    </div>
                  </>
                ) : selected === SELECT_OUTPUT ? (
                  <>
                    <CardTitle className="text-base">Output</CardTitle>
                    <CardDescription>
                      Leave empty to return the last step’s output, or combine values (e.g.{' '}
                      <code>{'{"summary": "{{steps.write.output}}"}'}</code>).
                    </CardDescription>
                    <Textarea
                      aria-label="Output template"
                      className="min-h-28 font-mono text-xs"
                      value={draft.output ?? ''}
                      disabled={!canEdit}
                      onChange={(e) => {
                        const { output: _drop, ...rest } = draft;
                        setDraft(
                          e.target.value === '' ? rest : { ...rest, output: e.target.value },
                        );
                      }}
                    />
                    <p className="text-muted-foreground text-xs">
                      Steps:{' '}
                      <span className="font-mono">
                        {graphIds(draft.graph)
                          .map((id) => `{{steps.${id}.output}}`)
                          .join(' ')}
                      </span>
                    </p>
                  </>
                ) : (
                  <StepInspector
                    definition={draft}
                    selected={selected}
                    resources={resources}
                    selfId={w.id}
                    disabled={!canEdit}
                    onChange={(def) => {
                      setDraft(def);
                      setBreaks(withGraph(model, def.graph).breaks);
                    }}
                    onSelect={setSelected}
                  />
                )}
              </TabsContent>
              <TabsContent value="run" forceMount className="data-[state=inactive]:hidden">
                {canEdit ? (
                  <WorkflowRunPanel
                    tenantId={tenantId}
                    workflow={w}
                    draft={dirty ? saved : null}
                    selected={selected}
                    loadRunId={loadRunId}
                    onLive={onLive}
                    onRun={onRun}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Running workflows requires the admin role.
                  </p>
                )}
              </TabsContent>
              <TabsContent value="history">
                <RunHistory
                  tenantId={tenantId}
                  workflowId={workflowId}
                  activeRunId={run?.id ?? null}
                  onPick={(r) => {
                    if (canEdit) {
                      setLoadRunId(r.id);
                      setTab('run');
                    } else {
                      setRun(r);
                      setLive(fromResults(r.steps));
                    }
                  }}
                />
              </TabsContent>
              <TabsContent value="schedules">
                <WorkflowSchedules tenantId={tenantId} workflow={w} canEdit={canEdit} />
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>
    </>
  );
}

export function WorkflowDetailPage() {
  const { workflowId } = useParams({ from: '/app/workflows/$workflowId' });
  return (
    <RequireTenant>
      {(tenant) => (
        <WorkflowEditor tenantId={tenant.id} role={tenant.role} workflowId={workflowId} />
      )}
    </RequireTenant>
  );
}
